import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';
import SeeItLive from './SeeItLive';
import AbbrTip from '@/components/AbbrTip';

const FAQS = [
  {
    q: 'Why can\u2019t I just use P/E for non-dividend stocks?',
    a: 'You often can, but the P/E ratio breaks in two common non-dividend situations. First: pre-profit companies. If EPS is negative or zero, P/E is either negative (mathematically valid, financially meaningless) or undefined. That covers most early-stage biotech, a lot of clinical-stage medtech, and any high-growth software still investing ahead of profitability. Second: extreme-growth companies where trailing earnings badly understate the future. Tesla trades near a trailing P/E of 413 right now — that number alone tells you almost nothing about whether the stock is expensive relative to what the business will earn two years from now. That\u2019s where P/S, EV/EBITDA, and PEG step in.',
  },
  {
    q: 'Is a low P/S always cheap and a high P/S always expensive?',
    a: 'No — P/S has to be read against the business\u2019s gross margin and growth rate. A grocer with 3% gross margins can\u2019t sustain a P/S above 1 for long, because there\u2019s no profit trapped inside each dollar of sales to reward investors. A software company with 80% gross margins can rationally trade at a P/S of 15 or higher, because most of every incremental dollar of revenue will drop to the bottom line at scale. So a P/S of 2 is expensive for a supermarket but cheap for enterprise software. Always compare a stock\u2019s P/S to its own industry median before drawing conclusions — the &ldquo;vs sector&rdquo; comparison on DiviDrip\u2019s Valuation Multiples card does this for you.',
  },
  {
    q: 'Why does DiviDrip show P/E even for non-dividend stocks that also use P/S and PEG?',
    a: 'Because for every non-dividend stock that IS already profitable, P/E is still the most widely-cited number on Wall Street — and having it visible lets you sanity-check the P/S read. Amazon, Netflix, Meta, Alphabet, and Nvidia all have real, positive earnings today, and their P/E ratios (around 29, 23, 21, 27, and 30 respectively as of this writing) tell you the market is pricing them like premium compounders rather than moonshots. The Valuation Multiples card intentionally shows all four (Trailing P/E, Forward P/E, P/S, PEG) so you can triangulate. If P/E and P/S agree that a stock looks stretched, that\u2019s a stronger signal than either metric alone.',
  },
  {
    q: 'What is EV/EBITDA and when should I care about it?',
    a: 'EV/EBITDA divides Enterprise Value (market cap plus debt minus cash) by EBITDA (earnings before interest, taxes, depreciation, and amortization). The point is to strip out capital-structure and tax-regime noise so you can compare companies apples-to-apples across countries, sectors, and levels of leverage. Investment bankers use it more than P/E because it\u2019s the multiple actual acquirers pay when they buy a business outright. It\u2019s especially useful for capital-intensive non-dividend stocks — think Uber, Netflix pre-2020, cable and telecom rollups, or capex-heavy industrials — where large depreciation charges distort GAAP earnings. Rough guideposts: below 8 is generally cheap, 8-15 is fair, above 18 is priced for growth. It\u2019s directional, not deterministic — always cross-reference the peer group.',
  },
  {
    q: 'How do I read PEG in practice?',
    a: 'PEG = P/E divided by the expected earnings growth rate expressed as a whole-number percent. Classic interpretation is that PEG near 1.0 means the price is roughly fair for the growth on offer, PEG below 1.0 is potentially undervalued, and PEG above 1.0 means investors are paying up for the growth. But PEG is only as good as the growth estimate feeding it. Salesforce\u2019s current PEG of 0.30 looks like a screaming bargain until you remember that PEG uses forward growth estimates that have been repeatedly cut over the last two years for enterprise software. Meta\u2019s PEG near 0.52 and Nvidia\u2019s near 0.33 look elite, but they\u2019re predicated on the market believing the AI-capex cycle keeps translating into earnings. Trust PEG most when the growth rate is trailing and verified — not when it\u2019s a broker projection.',
  },
  {
    q: 'What multiple do I use for a company with no revenue yet?',
    a: 'None of these work for a truly pre-revenue company (early-stage biotech, some clinical medtech, some pre-launch tech). You fall back to non-price-based frameworks: cash runway (how many quarters of operating expenses does the balance-sheet cash cover?), pipeline value (probability-weighted net present value of each drug candidate or product line), and the founder / operator record. This is the terrain where Forensic Safety (Altman Z-score, Beneish M) matters more than valuation multiples — if the business runs out of cash before it produces revenue, no multiple would have saved you. See our guide on Beneish + Altman for the safety gate.',
  },
];

export default function LearnValuationNonDividendStocks() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="Valuing Non-Dividend Stocks — P/S, EV/EBITDA, PEG Explained | TwylightCrow"
      description="A practical guide to the three multiples that replace P/E when a stock pays no dividend and reinvests its cash: Price-to-Sales, EV/EBITDA, and PEG. Real live numbers from AMZN, NVDA, META, NFLX, TSLA, CRM, PLTR — pulled straight from the DiviDrip Valuation Multiples card."
      canonical="https://twylightcrow.com/learn/valuation-non-dividend-stocks"
      guidePath="/learn/valuation-non-dividend-stocks"
      sectionLabel="Valuation &amp; Metrics"
      sectionAnchor="non-div-metrics"
      kicker="Learn · Non-Dividend Stocks"
      h1="Valuing Non-Dividend Stocks — P/S, EV/EBITDA, PEG"
      datePublished="2026-07-04"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/rule-of-40', label: 'The Rule of 40 — pair the multiples with the efficiency check' },
        { to: '/learn/free-cash-flow-growth', label: 'Free Cash Flow Growth — the honest measure behind the multiples' },
        { to: '/learn/operating-momentum', label: 'Operating Momentum — the acceleration signal that lifts multiples' },
        { to: '/glossary#entry-p-s-price-to-sales', label: 'Glossary: P/S (Price-to-Sales)' },
        { to: '/glossary#entry-ev-ebitda', label: 'Glossary: EV/EBITDA' },
        { to: '/glossary#entry-peg-price-earnings-to-growth-ratio', label: 'Glossary: PEG Ratio' },
      ]}
    >
      <p>
        The P/E ratio is the first valuation number most investors learn,
        and for a large slice of the dividend-paying universe it does
        exactly the job it was designed for. But P/E collapses the
        moment a company is deliberately reinvesting so hard that its
        earnings are suppressed &mdash; or reinvesting so hard that it
        doesn&rsquo;t have earnings yet at all. That&rsquo;s the world
        most non-dividend stocks live in: growth over payout, capacity
        over cash return, market share over margin.
      </p>
      <p>
        DiviDrip by TwylightCrow surfaces four valuation numbers on the
        <strong>{' '}Valuation Multiples{' '}</strong>card inside every
        Stock Metrics tab: Trailing P/E, Forward P/E, P/S, and PEG. Each
        one is compared against the sector-peer median so a number never
        floats in isolation. This guide walks through what each multiple
        answers, when to trust it, and where it fails &mdash; using live
        readings from real tickers on the platform.
      </p>

      <h2>The four multiples at a glance</h2>
      <table className="learn-table">
        <thead>
          <tr>
            <th>Multiple</th>
            <th>Formula</th>
            <th>Best used for</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">P/E (Trailing &amp; Forward)</td>
            <td className="font-mono">Price &divide; EPS</td>
            <td>Profitable companies with stable, ongoing earnings. Breaks when EPS is negative or heavily suppressed by reinvestment.</td>
          </tr>
          <tr>
            <td className="font-semibold">P/S (Price-to-Sales)</td>
            <td className="font-mono">Market Cap &divide; Annual Revenue</td>
            <td>Pre-profit or thin-margin growth companies. Ignores the earnings problem by anchoring on the top line.</td>
          </tr>
          <tr>
            <td className="font-semibold">EV/EBITDA</td>
            <td className="font-mono">(Market Cap + Debt &minus; Cash) &divide; EBITDA</td>
            <td>Capital-structure-agnostic acquirer&rsquo;s multiple. The go-to for capex-heavy or debt-heavy businesses.</td>
          </tr>
          <tr>
            <td className="font-semibold">PEG</td>
            <td className="font-mono">P/E &divide; Earnings Growth %</td>
            <td>Ranking profitable growth companies against each other. Only as good as the growth estimate feeding it.</td>
          </tr>
        </tbody>
      </table>
      <p>
        For deeper mechanics on each metric, use the Glossary links in
        the Related section &mdash; those entries carry the tier tables,
        formula variants, and caveats in full. This guide is the
        applied lens: how the four numbers behave together on real
        non-dividend tickers.
      </p>

      <h2>Price-to-Sales: the workhorse for growth stocks</h2>
      <p>
        P/S is the multiple you reach for first when a company has
        revenue but not yet meaningful earnings. It sidesteps every
        accounting choice around depreciation, stock-based
        compensation, tax rate, and capitalisation policy by anchoring
        on the top line &mdash; the number hardest to fake and easiest
        to compare across companies.
      </p>
      <p>
        The trap is that a P/S number in isolation tells you almost
        nothing. A grocer with a 3% gross margin has no room in each
        sales dollar to reward shareholders &mdash; a software company
        with 80% gross margins does. That&rsquo;s why P/S must always
        be read against the peer group. DiviDrip&rsquo;s Valuation
        Multiples card sits right next to the sector-median tile so
        the comparison is one glance away.
      </p>

      <h3>Live readings across the growth spectrum</h3>
      <table className="learn-table">
        <thead>
          <tr>
            <th>Ticker</th>
            <th>P/S</th>
            <th>What the number is telling you</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">
              <div className="flex items-center gap-2 flex-wrap">
                <span>CRM (Salesforce)</span>
                <SeeItLive ticker="CRM" tab="stock-metrics" tone="emerald" label="Live &rarr;" />
              </div>
            </td>
            <td className="font-mono text-emerald-600">2.9</td>
            <td>Mature enterprise-software compounder. P/S is well below the software-sector median &mdash; the market is treating CRM more like a slowing growth story than a fast one.</td>
          </tr>
          <tr>
            <td className="font-semibold">
              <div className="flex items-center gap-2 flex-wrap">
                <span>AMZN (Amazon)</span>
                <SeeItLive ticker="AMZN" tab="stock-metrics" tone="emerald" label="Live &rarr;" />
              </div>
            </td>
            <td className="font-mono text-emerald-600">3.4</td>
            <td>Blended P/S kept down by the low-margin retail half of the business. The AWS half alone would command a much higher standalone multiple &mdash; a good example of why P/S needs a segment-level read for conglomerates.</td>
          </tr>
          <tr>
            <td className="font-semibold">
              <div className="flex items-center gap-2 flex-wrap">
                <span>NFLX (Netflix)</span>
                <SeeItLive ticker="NFLX" tab="stock-metrics" tone="amber" label="Live &rarr;" />
              </div>
            </td>
            <td className="font-mono text-amber-600">6.5</td>
            <td>Streaming leader now valued as a mature growth compounder. P/S has compressed from double-digits during the 2020-21 subscriber-growth peak as revenue growth normalised.</td>
          </tr>
          <tr>
            <td className="font-semibold">
              <div className="flex items-center gap-2 flex-wrap">
                <span>META (Meta Platforms)</span>
                <SeeItLive ticker="META" tab="stock-metrics" tone="amber" label="Live &rarr;" />
              </div>
            </td>
            <td className="font-mono text-amber-600">6.8</td>
            <td>Premium ad-network economics. The market is paying for the ad business, with a haircut for the still-unprofitable Reality Labs segment. Sector-median P/S for Communication Services is below 3.</td>
          </tr>
          <tr>
            <td className="font-semibold">
              <div className="flex items-center gap-2 flex-wrap">
                <span>GOOGL (Alphabet)</span>
                <SeeItLive ticker="GOOGL" tab="stock-metrics" tone="amber" label="Live &rarr;" />
              </div>
            </td>
            <td className="font-mono text-amber-600">10.6</td>
            <td>Full-stack ad-and-cloud premium. P/S above Meta&rsquo;s reflects investor confidence that AI compute (Gemini + TPU + Cloud) will show up on the top line before it eats the margin.</td>
          </tr>
          <tr>
            <td className="font-semibold">
              <div className="flex items-center gap-2 flex-wrap">
                <span>TSLA (Tesla)</span>
                <SeeItLive ticker="TSLA" tab="stock-metrics" tone="amber" label="Live &rarr;" />
              </div>
            </td>
            <td className="font-mono text-amber-600">15.4</td>
            <td>Traded as a robotaxi / energy-plus-AI story, not as a car company. Auto peers trade at P/S below 1. The gap tells you the price is discounting future revenue lines that don&rsquo;t yet exist.</td>
          </tr>
          <tr>
            <td className="font-semibold">
              <div className="flex items-center gap-2 flex-wrap">
                <span>NVDA (Nvidia)</span>
                <SeeItLive ticker="NVDA" tab="stock-metrics" tone="rose" label="Live &rarr;" />
              </div>
            </td>
            <td className="font-mono text-rose-600">18.4</td>
            <td>Elite. Semiconductor peers trade around 4-6. The premium is the AI-compute lock-in narrative &mdash; if it breaks, this is the multiple most exposed.</td>
          </tr>
          <tr>
            <td className="font-semibold">
              <div className="flex items-center gap-2 flex-wrap">
                <span>CRWD (CrowdStrike)</span>
                <SeeItLive ticker="CRWD" tab="stock-metrics" tone="rose" label="Live &rarr;" />
              </div>
            </td>
            <td className="font-mono text-rose-600">33.6</td>
            <td>Best-in-class cybersecurity growth premium. The stock rewards the growth &mdash; but at 33x sales, every quarter of decelerating billings punches through the multiple hard.</td>
          </tr>
          <tr>
            <td className="font-semibold">
              <div className="flex items-center gap-2 flex-wrap">
                <span>PLTR (Palantir)</span>
                <SeeItLive ticker="PLTR" tab="stock-metrics" tone="rose" label="Live &rarr;" />
              </div>
            </td>
            <td className="font-mono text-rose-600">58.9</td>
            <td>Off the chart. This kind of P/S has historically been sustained only by companies whose revenue growth accelerates for multiple years in a row. History says the base rate for sustaining P/S above 40 is low.</td>
          </tr>
        </tbody>
      </table>
      <p className="text-[11px] text-muted-foreground italic">
        Values pulled live from the platform&rsquo;s Massive-sourced
        ratio cache. Open the Stock Metrics tab on any of these tickers
        to see the current numbers alongside sector-median comparisons.
      </p>

      <h2>EV/EBITDA: the acquirer&rsquo;s lens</h2>
      <p>
        Two companies can have identical P/E ratios and yet be very
        differently priced &mdash; because one is funded almost entirely
        with equity and the other carries meaningful debt. EV/EBITDA
        fixes that by adding debt back onto the numerator (and taking
        cash off) so you&rsquo;re comparing total business value, not
        just the equity slice.
      </p>
      <p>
        Where it earns its keep for non-dividend investors is in
        capex-heavy stories. GAAP earnings are depressed by
        depreciation charges on data centers, factories, streaming
        catalogs, or vehicle fleets &mdash; but those depreciation
        charges are non-cash. EBITDA adds them back to get closer to
        the pre-investment cash economics. When you&rsquo;re trying to
        judge whether Netflix&rsquo;s pre-2020 content-driven cash
        losses were rational, or whether Uber&rsquo;s expansion
        machinery is finally scaling, EV/EBITDA is a cleaner lens than
        P/E.
      </p>
      <p>
        The rough tier map inside the Glossary entry: below 8 is
        generally cheap for most industries, 8-15 is fair, above 18 is
        priced for growth or a premium franchise. But sector matters
        enormously &mdash; utilities structurally sit at 10-14, software
        can sustain 20+ during expansion phases, and cyclicals swing
        wildly around their trough and peak years. Never read EV/EBITDA
        without the peer-group context.
      </p>

      <h2>PEG: adjusting valuation for growth</h2>
      <p>
        P/E on its own doesn&rsquo;t tell you whether a 30x multiple is
        justified. PEG (P/E divided by earnings growth rate) attempts to
        make P/Es comparable across companies growing at different
        speeds. The classic reading, from Peter Lynch&rsquo;s{' '}
        <em>One Up on Wall Street</em>, is that a PEG near 1.0 is fair,
        under 1.0 is potentially undervalued, and over 1.0 means the
        market is paying up for growth.
      </p>
      <p>
        PEG&rsquo;s biggest weakness is the growth estimate that goes
        into it. Broker forward-growth forecasts have a bias toward
        optimism and can be revised sharply after a single guide-down.
        A useful discipline: any time PEG looks &ldquo;too good to be
        true,&rdquo; cross-reference against the company&rsquo;s
        3-year and 5-year trailing revenue and EPS CAGRs before
        trusting it.
      </p>

      <h3>Live PEG readings</h3>
      <table className="learn-table">
        <thead>
          <tr>
            <th>Ticker</th>
            <th>PEG</th>
            <th>Read</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">NVDA</td>
            <td className="font-mono text-emerald-600">0.33</td>
            <td>Growth expectations still running well ahead of the multiple. The single most important sanity check on this reading is whether the AI-capex cycle&rsquo;s forward growth estimate holds up.</td>
          </tr>
          <tr>
            <td className="font-semibold">CRM</td>
            <td className="font-mono text-emerald-600">0.30</td>
            <td>Deceptively low. Watch the forward growth rate quarter-over-quarter &mdash; if guidance is cut again, PEG will re-rate upward without the P/E moving at all.</td>
          </tr>
          <tr>
            <td className="font-semibold">META</td>
            <td className="font-mono text-emerald-600">0.52</td>
            <td>Ad-business economics + AI infrastructure paying off through operating leverage. Priced fairly cheaply for the growth on offer.</td>
          </tr>
          <tr>
            <td className="font-semibold">NFLX</td>
            <td className="font-mono text-amber-600">1.00</td>
            <td>Textbook &ldquo;priced for the growth.&rdquo; No obvious edge from PEG alone &mdash; use other lenses (FCF trajectory, subscriber growth, ad-tier ramp).</td>
          </tr>
          <tr>
            <td className="font-semibold">AMZN</td>
            <td className="font-mono text-amber-600">1.26</td>
            <td>Slight premium to fair. Reasonable for a diversified platform where retail growth is slow but AWS and ad revenue are compounding faster.</td>
          </tr>
          <tr>
            <td className="font-semibold">GOOGL</td>
            <td className="font-mono text-amber-600">1.57</td>
            <td>Paying up for growth. Reasonable if you believe the Cloud + Gemini re-acceleration story, expensive if you don&rsquo;t.</td>
          </tr>
        </tbody>
      </table>

      <h2>Reading the multiples together</h2>
      <p>
        No single multiple wins on its own. The value of the
        Valuation Multiples card is the composite picture. A few
        common patterns worth naming:
      </p>
      <ul>
        <li>
          <strong>P/E and P/S both above sector median, PEG below 1:</strong>{' '}
          Priced for growth, but priced fairly if the growth actually
          shows up. Example fingerprint: current NVDA, META. Track
          quarterly guidance discipline.
        </li>
        <li>
          <strong>P/S in line with sector, PEG well above 1:</strong>{' '}
          The market is paying a growth premium the top line
          doesn&rsquo;t yet reflect. Usually a re-rating waiting to
          happen &mdash; either the growth accelerates or the multiple
          compresses. Cross-reference with{' '}
          <Link to="/learn/operating-momentum">Operating Momentum</Link>{' '}
          before deciding which.
        </li>
        <li>
          <strong>P/E extreme, P/S moderate:</strong> Reinvestment
          suppressing near-term earnings. This is Amazon 2010-2020 or
          Netflix 2015-2019. If Free Cash Flow is genuinely improving,
          the P/E is misleading. Read the{' '}
          <Link to="/learn/free-cash-flow-growth">Free Cash Flow Growth guide</Link>{' '}
          before writing off a stock on P/E alone.
        </li>
        <li>
          <strong>P/S extreme, no earnings:</strong> Pure narrative
          premium. History says this is the fastest tier to compress
          when growth decelerates. Palantir at 58x sales and Tesla at
          15x sales both sit in this bucket right now. Position size
          accordingly.
        </li>
      </ul>

      <h2>Historical inflections worth remembering</h2>

      <h3>Amazon (AMZN) &mdash; a 20-year lesson in why P/E was the wrong lens</h3>
      <p>
        For most of the 2004-2015 window Amazon carried trailing P/Es
        above 100 &mdash; sometimes above 500. Traditional value
        investors screening on P/E kept rejecting the stock. But
        Bezos was intentionally suppressing GAAP earnings to
        reinvest into AWS, logistics, and Prime. Revenue was
        compounding at 25-35% annually, and P/S never went above 3
        for long. Investors who anchored on the P/S read got the
        signal that GAAP earnings failed to give. Amazon returned
        roughly 1,900% over the decade from 2010 to 2020, and the
        best single-metric explanation is that P/S never priced in
        the level of platform dominance the business was building.
      </p>

      <h3>Netflix (NFLX) &mdash; the great multiple-compression of 2022</h3>
      <p>
        In early 2021 Netflix traded near a P/S of 10 with revenue
        growth still comfortably above 20%. When the Q1 2022
        earnings release showed the first quarterly subscriber loss
        in more than a decade, the market re-rated the growth story
        almost overnight. The P/S compressed from 10 to below 3
        inside twelve months even though revenue growth stayed
        positive. The lesson: at a P/S of 10, the price was already
        assuming continued acceleration &mdash; and any deceleration,
        even mild, breaks the thesis. Today NFLX has settled around a
        P/S of 6.5 as an ad-supported mature-growth story.
      </p>

      <h3>Salesforce (CRM) &mdash; the multiple that rewarded discipline</h3>
      <p>
        Salesforce spent most of 2015-2021 at a P/S above 8, funded
        by aggressive M&amp;A (MuleSoft, Tableau, Slack). When the
        2022 macro turn forced a company-wide focus on operating
        leverage, revenue growth moderated but margins improved.
        Today CRM sits near a P/S of 2.9 &mdash; the lowest reading
        in a decade &mdash; alongside a Rule of 40 score that has
        stayed cleanly in Healthy tier. The setup is unusual: a
        multiple that&rsquo;s compressed toward mature-industrials
        levels while the efficiency picture stayed intact. Whether
        that&rsquo;s a value setup or a value trap turns entirely on
        whether AI-agent revenue actually re-accelerates the top
        line in the next four quarters.
      </p>

      <h2>The 60-second valuation check</h2>
      <ol>
        <li>
          Open the stock modal and select the Stock Metrics tab. The
          Valuation Multiples card is the second tile from the top.
        </li>
        <li>
          Read all four tiles at once &mdash; Trailing P/E, Forward
          P/E, P/S, PEG &mdash; against the sector-median comparison.
        </li>
        <li>
          Ask three questions in order: (1) Is the company
          <em>{' '}profitable{' '}</em>today? If no, skip P/E and PEG
          and lean on P/S plus forensic safety. (2) Is P/S materially
          above the sector median? If yes, the story better include
          real growth acceleration. (3) Does PEG line up with the
          trailing 3-year growth, not just the broker forward
          estimate?
        </li>
        <li>
          Cross-reference with the{' '}
          <Link to="/learn/rule-of-40">Rule of 40</Link> and the{' '}
          <Link to="/learn/free-cash-flow-growth">FCF Growth</Link>{' '}
          check. The multiples answer &ldquo;how much are you paying?&rdquo;
          Those two answer &ldquo;what are you paying for?&rdquo;
          You need both to size a position with conviction.
        </li>
      </ol>

      <h2>Where these multiples sit in the bigger picture</h2>
      <p>
        The Valuation Multiples card is meant to be the fastest
        possible sanity check &mdash; four numbers that anchor
        whatever fuller analysis you do next. None of them feed the
        Capital Reinvestment Score directly, because valuation is
        deliberately kept out of the composite: a great business at a
        bad price is a different problem than a bad business at any
        price, and the Capital Reinvestment Score answers the second
        question, not the first. Use the multiples to judge whether
        the entry price is defensible, then use the Capital Analytics
        tab to judge whether the business is worth owning at any
        price.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }, i) => (
          <div key={i} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Try it</h2>
      <p>
        Pull up a non-dividend name you&rsquo;re curious about on the{' '}
        <Link to="/dashboard">Dashboard</Link> and open the Stock
        Metrics tab. Read the four multiples top-to-bottom, then flip
        to Capital Analytics for the efficiency and quality picture.
        Compare a mature compounder (ADBE or AAPL if you allow the
        dividend-payer exception), a heavy reinvestor (AMZN), an
        elite-growth story (NVDA or PLTR), and a compression casualty
        (CRM). The shape of the four numbers together will tell you
        more about market sentiment than any single one on its own.
      </p>

      <p className="mt-6">
        For the full definitions, tier tables, and formula caveats,
        see the{' '}
        <AbbrTip term="P_S">P/S</AbbrTip>,{' '}
        <AbbrTip term="EV_EBITDA">EV/EBITDA</AbbrTip>, and{' '}
        <AbbrTip term="PEG">PEG</AbbrTip>{' '}
        glossary entries.
      </p>
      <p className="text-xs text-muted-foreground italic">
        This guide is educational. Multiples are directional signals,
        not price targets &mdash; pair them with forensic safety
        checks and a read of the latest 10-Q before committing
        capital. Historical performance is not a forecast.
      </p>
    </LearnPage>
  );
}
